import React, { useMemo, useState } from 'react';
import { useIeKitLabels } from '@/hooks/useIeKitLabels';
import { PAGE_SIZE_OPTIONS } from '@/constants/pagination';
import {
  Alert,
  Box,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Typography,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import { usePHRItems, useTHRItems } from '@/hooks';
import type { PHRItem, THRItem } from '@/services/iem/types';
import { httpClient } from '@/utils/httpClient';
import { TransactionDetailModal } from '@/components/transactions/TransactionDetailModal';

const CENTERED = { align: 'center' as const, headerAlign: 'center' as const };

type AssetTransactionEntity = {
  id: string;
  entity_data: {
    asset_id?: string | null;
    transaction_id?: string | null;
    transaction_type?: string | null;
    date_processed?: string | null;
  } | null;
  created_at: string;
};

type AssetTransactionEntry = {
  transactionId: string;
  transactionType: string;
  date: string;
};

/** Short labels for transaction types shown in the column */
const TX_TYPE_LABELS: Record<string, string> = {
  ISSUE_OUT: 'Issue Out',
  EXCHANGE: 'Exchange',
  THR_DUE_DATE_EXTENDED: 'Due Date Extended',
  THR_TO_PHR_CONVERTED: 'THR → PHR',
  PURCHASE_REQUISITION_CREATE: 'PR Created',
  PURCHASE_REQUISITION_RECEIVE: 'PR Received',
  CORRECTION_FULL_DOCUMENT: 'Correction',
  CORRECTION_ITEM_LEVEL: 'Correction',
  MAINTENANCE_SEND: 'Maint. Send',
  MAINTENANCE_RECEIVE: 'Maint. Receive',
};

const formatTxType = (type: string): string => TX_TYPE_LABELS[type] ?? type.replaceAll('_', ' ');

/** Transaction types that mean the item left the hand receipt — filter these out */
const EXCLUDED_TX_TYPES = new Set([
  'TURN_IN',
  'DISPOSAL',
  'NON_RECOVERABLE_CLEAR',
  'INDIVIDUAL_CLEARED',
]);

const formatShortDate = (value: string): string => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString();
};

/**
 * Fetch all asset_transaction entities for the given asset IDs and build a
 * mapping from asset_id → list of { transactionId, transactionType, date }.
 */
const useAssetTransactionMap = (assetIds: string[]) => {
  return useQuery({
    queryKey: ['asset-transaction-map', ...assetIds.slice().sort()],
    enabled: assetIds.length > 0,
    staleTime: 60_000,
    queryFn: async (): Promise<Map<string, AssetTransactionEntry[]>> => {
      const params = new URLSearchParams({
        entity_type: 'eq.asset_transaction',
        is_current: 'eq.true',
        select: 'id,entity_data,created_at',
        order: 'created_at.desc',
      });
      params.set('or', `(${assetIds.map((id) => `entity_data->>asset_id.eq.${id}`).join(',')})`);

      const records = await httpClient.get<AssetTransactionEntity[]>(
        `/api/rest/data/v1/entities?${params.toString()}`,
      );

      const map = new Map<string, AssetTransactionEntry[]>();
      for (const record of records) {
        const assetId = record.entity_data?.asset_id;
        const transactionId = record.entity_data?.transaction_id;
        const transactionType = record.entity_data?.transaction_type;
        if (
          assetId &&
          transactionId &&
          transactionType &&
          !EXCLUDED_TX_TYPES.has(transactionType)
        ) {
          const list = map.get(assetId) ?? [];
          const dateProcessed = record.entity_data?.date_processed;
          list.push({
            transactionId,
            transactionType,
            date: dateProcessed && dateProcessed.trim() ? dateProcessed : record.created_at,
          });
          map.set(assetId, list);
        }
      }
      return map;
    },
  });
};

const dateTimeFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
});

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return dateTimeFormatter.format(date);
};

interface PersonHandReceiptsTabProps {
  personId: string;
  type: 'phr' | 'thr';
}

export const PersonHandReceiptsTab: React.FC<PersonHandReceiptsTabProps> = ({ personId, type }) => {
  const { columnLabel: ieKitColumnLabel } = useIeKitLabels('front_window');
  const phrQuery = usePHRItems(personId, { status: 'neq.closed' });
  const thrQuery = useTHRItems(personId, { status: 'neq.closed' });
  const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null);
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [popoverEntries, setPopoverEntries] = useState<AssetTransactionEntry[]>([]);

  const query = type === 'phr' ? phrQuery : thrQuery;
  const label = type === 'phr' ? 'Permanent Hand Receipt' : 'Temporary Hand Receipt';

  const rawRows = query.data ?? [];

  // Aggregate rows that share the same visible columns (except qty/transactions).
  // For PHR: combine only when serial_number is empty and all other display columns match.
  // For THR: combine when lin, nsn, item, issueDate, dueDate, status, hasExtension match.
  const rows = useMemo(() => {
    if (type === 'thr') {
      const grouped = new Map<string, THRItem & { _assetIds: string[] }>();
      for (const row of rawRows as THRItem[]) {
        const key = [
          row.lin ?? '',
          row.nsn,
          row.item,
          row.issueDate,
          row.dueDate,
          row.status,
          String(row.hasExtension),
        ].join('\x1f');
        const existing = grouped.get(key);
        if (existing) {
          existing.qty += row.qty;
          if (row.asset_id) existing._assetIds.push(row.asset_id);
        } else {
          grouped.set(key, {
            ...row,
            _assetIds: row.asset_id ? [row.asset_id] : [],
          });
        }
      }
      return Array.from(grouped.values());
    }
    // PHR: combine rows without serial numbers when all display columns match
    const grouped = new Map<string, PHRItem & { _assetIds: string[]; _qty: number }>();
    for (const row of rawRows as PHRItem[]) {
      // Serialized items stay as individual rows
      if (row.serial_number) {
        grouped.set(row.id, { ...row, _assetIds: row.asset_id ? [row.asset_id] : [], _qty: 1 });
        continue;
      }
      const key = [
        row.ie_kit_name ?? '',
        row.lin ?? '',
        row.nsn,
        row.item,
        row.issueDate,
        row.condition,
        row.status,
      ].join('\x1f');
      const existing = grouped.get(key);
      if (existing) {
        existing._qty += 1;
        if (row.asset_id) existing._assetIds.push(row.asset_id);
      } else {
        grouped.set(key, {
          ...row,
          _assetIds: row.asset_id ? [row.asset_id] : [],
          _qty: 1,
        });
      }
    }
    return Array.from(grouped.values());
  }, [rawRows, type]);

  // Collect asset_ids for transaction lookup
  const assetIds = useMemo(
    () => rawRows.map((r) => r.asset_id).filter((id): id is string => Boolean(id)),
    [rawRows],
  );
  const { data: txMap } = useAssetTransactionMap(assetIds);

  const transactionColumn: GridColDef = {
    field: '_transaction',
    headerName: 'Transactions',
    width: 130,
    sortable: false,
    filterable: false,
    ...CENTERED,
    renderCell: (params) => {
      // For aggregated THR rows, collect transactions from all merged asset IDs
      const rowAssetIds: string[] =
        (params.row._assetIds as string[] | undefined) ??
        (params.row.asset_id ? [params.row.asset_id] : []);
      const entries = rowAssetIds.flatMap((id) => txMap?.get(id) ?? []);
      if (entries.length === 0) return '—';

      // Single transaction — open directly
      if (entries.length === 1) {
        const entry = entries[0]!;
        return (
          <Chip
            label={formatTxType(entry.transactionType)}
            size="small"
            variant="outlined"
            clickable
            onClick={(e) => {
              e.stopPropagation();
              setSelectedTransactionId(entry.transactionId);
            }}
            sx={{ fontSize: '0.7rem', height: 22 }}
          />
        );
      }

      // Multiple transactions — show count chip that opens a popover
      return (
        <Chip
          label={`${entries.length} transactions`}
          size="small"
          variant="outlined"
          clickable
          onClick={(e) => {
            e.stopPropagation();
            setPopoverAnchor(e.currentTarget);
            setPopoverEntries(entries);
          }}
          sx={{ fontSize: '0.7rem', height: 22 }}
        />
      );
    },
  };

  const phrColumns: GridColDef<PHRItem>[] = [
    { field: 'ie_kit_name', headerName: ieKitColumnLabel, flex: 1, minWidth: 120, sortable: true },
    { field: 'lin', headerName: 'LIN', flex: 0.7, minWidth: 80, sortable: true },
    { field: 'nsn', headerName: 'NSN', flex: 1, minWidth: 120, sortable: true },
    { field: 'item', headerName: 'Nomenclature', flex: 2, minWidth: 200, sortable: true },
    { field: 'serial_number', headerName: 'Serial Number', flex: 1, minWidth: 120, sortable: true },
    {
      field: '_qty',
      headerName: 'Qty',
      flex: 0.4,
      minWidth: 50,
      type: 'number',
      sortable: true,
      ...CENTERED,
    },
    { field: 'issueDate', headerName: 'Issue Date', flex: 0.8, minWidth: 100, sortable: true },
    { field: 'condition', headerName: 'Condition', flex: 0.7, minWidth: 90, sortable: true },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.7,
      minWidth: 90,
      sortable: true,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value === 'Assigned' ? 'success' : 'default'}
          variant="outlined"
        />
      ),
    },
    transactionColumn,
  ];

  const thrColumns: GridColDef<THRItem>[] = [
    { field: 'lin', headerName: 'LIN', flex: 0.7, minWidth: 80, sortable: true },
    { field: 'nsn', headerName: 'NSN', flex: 1, minWidth: 120, sortable: true },
    { field: 'item', headerName: 'Nomenclature', flex: 2, minWidth: 200, sortable: true },
    {
      field: 'qty',
      headerName: 'Qty',
      flex: 0.4,
      minWidth: 50,
      type: 'number',
      sortable: true,
      ...CENTERED,
    },
    { field: 'issueDate', headerName: 'Issue Date', flex: 0.8, minWidth: 100, sortable: true },
    { field: 'dueDate', headerName: 'Due Date', flex: 0.8, minWidth: 100, sortable: true },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.7,
      minWidth: 90,
      sortable: true,
      renderCell: (params) => {
        const row = params.row;
        const isOverdue = row.dueDate && row.dueDate !== '--' && new Date(row.dueDate) < new Date();
        return (
          <Chip
            label={isOverdue ? 'Overdue' : params.value}
            size="small"
            color={isOverdue ? 'error' : params.value === 'Active' ? 'success' : 'default'}
            variant="outlined"
          />
        );
      },
    },
    {
      field: 'hasExtension',
      headerName: 'Extended',
      flex: 0.6,
      minWidth: 80,
      sortable: true,
      renderCell: (params) =>
        params.value ? <Chip label="Yes" size="small" color="info" variant="outlined" /> : '—',
    },
    transactionColumn,
  ];

  const columns = type === 'phr' ? phrColumns : thrColumns;

  if (query.error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to load {label.toLowerCase()}: {query.error.message}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        {rows.length} {rows.length === 1 ? 'item' : 'items'}
      </Typography>
      <DataGrid
        rows={rows}
        columns={columns as GridColDef[]}
        loading={query.isLoading}
        autoHeight
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        disableRowSelectionOnClick
        density="compact"
        sx={{ minHeight: 200 }}
      />
      <TransactionDetailModal
        open={Boolean(selectedTransactionId)}
        transactionId={selectedTransactionId}
        onClose={() => setSelectedTransactionId(null)}
        formatDateTime={formatDateTime}
      />
      <Popover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={() => {
          setPopoverAnchor(null);
          setPopoverEntries([]);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <List dense sx={{ minWidth: 220, py: 0.5 }}>
          {popoverEntries
            .slice()
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
            .map((entry, idx) => (
              <ListItemButton
                key={`${entry.transactionId}-${idx}`}
                onClick={() => {
                  setPopoverAnchor(null);
                  setPopoverEntries([]);
                  setSelectedTransactionId(entry.transactionId);
                }}
              >
                <ListItemText
                  primary={`${formatTxType(entry.transactionType)} — ${formatShortDate(entry.date)}`}
                />
              </ListItemButton>
            ))}
        </List>
      </Popover>
    </Box>
  );
};
